/* eslint-disable max-lines */
import { useEffect, useMemo, useState } from 'react';
import PageHeader from '@components/layout/page-header';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../router/paths';
import { useGetModels } from '@utils/api/models/use-get-models';
import { useGetUsageLegacyBo } from '@utils/api/back-office/usage/usage-legacy';
import { formatDuration } from '@utils/date';
import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';
import DateInput from '@components/atoms/ui-kit/date-input.tsx';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params';

const formatDate = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getDefaultDates = () => {
  const current = new Date();
  const firstDay = new Date(current.getFullYear(), current.getMonth(), 1);
  return {
    period_start: formatDate(firstDay),
    period_end: formatDate(current)
  };
};

type Row = {
  _rowId: string;
  organization_id: number;
  organization_name: string;
  duration: string;
  month: string;
  year: string | number;
  language?: string;
};

export default function UsageLegacy() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { searchParams, setSearchParams } = useNewAppSearchParams();

  const [language, setLanguage] = useState(false);

  // Dates are optional initially; API hook will resolve defaults if missing.
  const [params, setParams] = useState({
    period_start: undefined as string | undefined,
    period_end: undefined as string | undefined,
    direction: 'desc' as 'asc' | 'desc',
    sort: 'duration',
    language: false
  });

  // Reset pagination when filters change (preserve sorting & q)
  useEffect(() => {
    const limit = searchParams.limit || '25';
    setSearchParams({
      offset: '0',
      limit,
      q: searchParams.q || undefined,
      sort: searchParams.sort || undefined,
      direction: searchParams.direction || undefined
    });
  }, [params.period_start, params.period_end, params.language]);

  const getMonthName = (m: number | undefined): string => {
    const names = [
      t('jan'),
      t('feb'),
      t('mar'),
      t('apr'),
      t('may'),
      t('jun'),
      t('jul'),
      t('aug'),
      t('sep'),
      t('oct'),
      t('nov'),
      t('dec')
    ];
    if (m === undefined || m < 0 || m > 11) return '-';
    return names[m];
  };

  const { data: supportedLanguages } = useGetModels();
  const { data, isLoading, isError } = useGetUsageLegacyBo({
    period_start: params.period_start,
    period_end: params.period_end,
    direction: params.direction,
    sort: params.sort,
    language: params.language
  });

  const defaultRange = getDefaultDates();

  // Keep date values as plain YYYY-MM-DD strings (no Date objects)
  const handleDateChange = (
    field: 'period_start' | 'period_end',
    value: string
  ) => {
    const v = value?.trim() || undefined;

    setParams(prev => {
      // If cleared, snap UI back to the default range immediately
      if (!v) {
        return {
          ...prev,
          period_start: defaultRange.period_start,
          period_end: defaultRange.period_end
        };
      }

      const next = { ...prev, [field]: v };

      // Keep range sane only when both are set
      if (next.period_start && next.period_end) {
        if (next.period_start > next.period_end) {
          if (field === 'period_start') next.period_end = next.period_start;
          else next.period_start = next.period_end;
        }
      }
      return next;
    });
  };

  const handleCellClick = (row: Row, field: string) => {
    if (field === 'organization_id' || field === 'organization_name') {
      navigate(PATHS(String(row.organization_id)).CUSTOMER_CONTENT);
    }
  };

  const languageMap: Record<string, string> = useMemo(() => {
    if (!language || !supportedLanguages?.result) return {};
    return supportedLanguages.result.reduce(
      (acc, { key, name }) => {
        acc[key] = name;
        return acc;
      },
      {} as Record<string, string>
    );
  }, [language, supportedLanguages]);

  const rows = useMemo<Row[]>(() => {
    if (!data) return [];

    return data.result.map(item => {
      const keyParts = [
        String(item.organization_id ?? ''),
        String(item.year ?? ''),
        String(item.month ?? ''),
        params.language ? String(item.language ?? '') : ''
      ];
      const _rowId = keyParts.join('|');

      return {
        _rowId,
        organization_id: item.organization_id,
        organization_name: item.organization_name ?? '-',
        duration: formatDuration(item.duration ?? 0),
        language:
          language && item.language ? (languageMap[item.language] ?? '-') : '',
        month:
          typeof item.month === 'number' ? getMonthName(item.month - 1) : '-',
        year: item.year ?? '-'
      };
    });
  }, [data, language, languageMap, params.language, t]);

  const columns = useMemo<DataGridColumn<Row>[]>(() => {
    const common = { enableSorting: true, enableFiltering: false };
    return [
      {
        header: t('id'),
        field: 'organization_id',
        maxWidth: 100,
        ...common,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('name'),
        field: 'organization_name',
        minWidth: 260,
        ...common,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('duration'),
        field: 'duration',
        maxWidth: 140,
        ...common,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('month'),
        field: 'month',
        maxWidth: 120,
        ...common,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('year'),
        field: 'year',
        maxWidth: 120,
        ...common,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      ...(language
        ? ([
            {
              header: t('language'),
              field: 'language',
              minWidth: 160,
              enableSorting: true,
              enableFiltering: false,
              cell: ({ getValue }) => String(getValue() ?? '')
            } as DataGridColumn<Row>
          ] as DataGridColumn<Row>[])
        : [])
    ];
  }, [t, language]);

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
    today.getDate()
  ).padStart(2, '0')}`;

  return (
    <div className="bg-app flex-1 px-4 py-2">
      <PageHeader title={t('usage')} caption={t('view-usage-activity-below')}>
        <div className="grid grid-cols-3 gap-4">
          <div className="flex justify-center items-center">
            <div className="col-span-12 sm:col-span-6 md:col-span-2 mt-4">
              <Checkbox
                id="language"
                label={t('Language')}
                checked={language}
                onChange={() => {
                  const next = !language;
                  setLanguage(next);
                  setParams(prev => ({ ...prev, language: next }));
                }}
                variant="primary"
                wrapperClass="relative flex items-center"
                className="!m-0"
              />
            </div>
          </div>

          <DateInput
            id="period_start"
            label={t('start-date')}
            value={params.period_start ?? ''}
            onChange={v => handleDateChange('period_start', v)}
            min="2000-01-01"
            // Max is end if set, otherwise today (UX aid only)
            max={params.period_end ?? todayStr}
            withShortcuts
            size="small"
            wrapperClass="w-full"
          />

          <DateInput
            id="period_end"
            label={t('end-date')}
            value={params.period_end ?? ''}
            onChange={v => handleDateChange('period_end', v)}
            // Min is start if set, otherwise earliest allowed
            min={params.period_start ?? '2000-01-01'}
            max={todayStr}
            withShortcuts
            size="small"
            wrapperClass="w-full"
          />
        </div>
      </PageHeader>

      <div className="w-full p-2 md:p-0">
        {isError && (
          <p className="px-4 py-2 text-sm text-red-600">
            {t('an-unexpected-error-occurred-please-try-again')}
          </p>
        )}
        <DataGridV2<Row>
          data={rows}
          columns={columns}
          enableRowSelection={false}
          enablePagination
          manualPagination
          enableSorting
          manualSorting
          enableFiltering={false}
          manualFiltering={false}
          totalItems={data?.total_results ?? rows.length}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          showColumnFilters={false}
          filterMode="popover"
          height="73vh"
          dense
          grid={false}
          striped
          loading={isLoading}
          getRowId={r => r._rowId}
          onCellClick={handleCellClick}
        />
      </div>
    </div>
  );
}
